import catalogJson from "./directory-categories.json";

/**
 * Catalogo canonico de categorias del directorio. Los identificadores son
 * estables (slug) y las subcategorias usan el prefijo del padre
 * (`padre.hijo`), de modo que un filtro general incluye sus subcategorias con
 * `id = p OR id LIKE 'p.%'` (misma regla que la funcion SQL
 * `directory_is_allowed_category`).
 *
 * `legacyEquivalences` mapea datos historicos de forma inequivoca (un termino
 * pertenece a UNA sola categoria; alimenta el backfill). `searchTerms` son
 * sinonimos de busqueda y pueden repetirse entre categorias.
 */

export interface DirectorySubcategory {
  id: string;
  label: string;
  parentId: string;
  legacyEquivalences: string[];
  searchTerms: string[];
}

export interface DirectoryCategory {
  id: string;
  label: string;
  legacyEquivalences: string[];
  searchTerms: string[];
  children: DirectorySubcategory[];
}

interface RawNode {
  id: string;
  label: string;
  legacyEquivalences?: string[];
  searchTerms?: string[];
  children?: RawNode[];
}

const rawCatalog = catalogJson as unknown as { version: number; categories: RawNode[] };

export const DIRECTORY_CATEGORY_CATALOG_VERSION = rawCatalog.version;

export const directoryCategories: DirectoryCategory[] = rawCatalog.categories.map((category) => ({
  id: category.id,
  label: category.label,
  legacyEquivalences: category.legacyEquivalences ?? [],
  searchTerms: category.searchTerms ?? [],
  children: (category.children ?? []).map((child) => ({
    id: child.id,
    label: child.label,
    parentId: category.id,
    legacyEquivalences: child.legacyEquivalences ?? [],
    searchTerms: child.searchTerms ?? [],
  })),
}));

export type DirectoryCategoryNode = DirectoryCategory | DirectorySubcategory;

/** Lista plana de todas las categorias y subcategorias. */
export const directoryCategoryIndex: DirectoryCategoryNode[] = directoryCategories.flatMap((category) => [
  category,
  ...category.children,
]);

const categoryById = new Map<string, DirectoryCategoryNode>();
for (const node of directoryCategoryIndex) categoryById.set(node.id, node);

/** Normaliza texto para comparaciones: minusculas, sin tildes, espacios colapsados. */
export function normalizeDirectoryText(value: string | null | undefined): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

const legacyEquivalenceMap = new Map<string, string>();
for (const node of directoryCategoryIndex) {
  for (const raw of node.legacyEquivalences) {
    const normalized = normalizeDirectoryText(raw);
    if (normalized) legacyEquivalenceMap.set(normalized, node.id);
  }
}

/** Todas las equivalencias historicas normalizadas (para tests / verificacion SQL). */
export const directoryLegacyEquivalences: ReadonlyArray<{ normalized: string; categoryId: string }> = [
  ...legacyEquivalenceMap.entries(),
].map(([normalized, categoryId]) => ({ normalized, categoryId }));

/** Ids permitidos (categoria + subcategorias). Espejo de `directory_is_allowed_category`. */
export const directoryAllowedCategoryIds: readonly string[] = directoryCategoryIndex.map((node) => node.id);

export function isDirectoryCategoryId(value: string | null | undefined): boolean {
  return typeof value === "string" && categoryById.has(value);
}

export function getDirectoryCategory(id: string | null | undefined): DirectoryCategoryNode | null {
  if (!id) return null;
  return categoryById.get(id) ?? null;
}

export function getDirectoryCategoryLabel(id: string | null | undefined): string {
  return getDirectoryCategory(id)?.label ?? "";
}

export function getDirectoryCategoryParentLabel(id: string | null | undefined): string | null {
  const node = getDirectoryCategory(id);
  if (!node || !("parentId" in node)) return null;
  return categoryById.get(node.parentId)?.label ?? null;
}

/** Ids que cubre un filtro de categoria general (incluye subcategorias). */
export function categoryIdsForFilter(id: string | null | undefined): string[] {
  const node = getDirectoryCategory(id);
  if (!node) return [];
  if ("children" in node) return [node.id, ...node.children.map((child) => child.id)];
  return [node.id];
}

/**
 * Mapea una etiqueta historica a un id de categoria. Devuelve `null` cuando no
 * hay equivalencia inequivoca: esos registros quedan como "heredados/pendientes
 * de clasificar" y NUNCA se reasignan por similitud textual.
 */
export function matchCategoryByLegacyLabel(label: string | null | undefined): string | null {
  const normalized = normalizeDirectoryText(label);
  if (!normalized) return null;
  return legacyEquivalenceMap.get(normalized) ?? null;
}

export interface DirectoryCategorySuggestion {
  id: string;
  label: string;
  parentLabel: string | null;
}

/** Sugiere categorias por etiqueta o sinonimos. Prioriza coincidencia exacta. */
export function suggestDirectoryCategories(query: string, limit = 6): DirectoryCategorySuggestion[] {
  const normalized = normalizeDirectoryText(query);
  if (!normalized) return [];

  const scored: Array<{ node: DirectoryCategoryNode; score: number }> = [];
  for (const node of directoryCategoryIndex) {
    let score = Number.POSITIVE_INFINITY;
    for (const term of [node.label, ...node.searchTerms]) {
      const normalizedTerm = normalizeDirectoryText(term);
      if (!normalizedTerm) continue;
      if (normalizedTerm === normalized) score = Math.min(score, 0);
      else if (normalizedTerm.startsWith(normalized)) score = Math.min(score, 1);
      else if (normalizedTerm.includes(normalized)) score = Math.min(score, 2);
    }
    if (Number.isFinite(score)) scored.push({ node, score });
  }

  scored.sort((a, b) => (a.score !== b.score ? a.score - b.score : a.node.label.localeCompare(b.node.label, "es")));

  return scored.slice(0, limit).map(({ node }) => ({
    id: node.id,
    label: node.label,
    parentLabel: "parentId" in node ? (categoryById.get(node.parentId)?.label ?? null) : null,
  }));
}
